import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logWarn } from "@/lib/log";

function Shell({ children }) {
  return <div className="min-h-screen flex items-center justify-center p-6 relative z-10"><div className="max-w-md w-full text-center rise">{children}</div></div>;
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshEnt } = useAuth();
  const [state, setState] = useState("checking");
  const [tier, setTier] = useState(null);
  useEffect(() => {
    const sid = params.get("session_id");
    let n = 0;
    let stop = false;
    let lastErr = null;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await http.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          const e = await refreshEnt();
          setTier(e?.tier);
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) {
        lastErr = e;
        logWarn("Payment status check failed, retrying", e);
      }
      if (++n >= 15) {
        setState("timeout");
        if (lastErr) toast.error(`We couldn't confirm your payment yet: ${formatError(lastErr)}`);
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshEnt]);

  return (
    <Shell>
      {state === "checking" && <><Loader2 className="h-10 w-10 animate-spin text-sky-400 mx-auto" /><p className="mt-4 text-neutral-400" data-testid="payment-checking">Confirming your payment…</p></>}
      {state === "paid" && (
        <>
          <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
          <h1 className="font-display text-3xl font-semibold mt-4" data-testid="payment-success-title">
            {tier ? <>You're on <span className="text-sky-300 capitalize">{tier}</span></> : "Payment confirmed"}
          </h1>
          <p className="text-neutral-400 mt-2">Your new features are unlocked. Happy studying!</p>
        </>
      )}
      {state === "failed" && <><XCircle className="h-12 w-12 text-red-400 mx-auto" /><h1 className="font-display text-3xl font-semibold mt-4">Payment didn't go through</h1></>}
      {state === "timeout" && <p className="text-neutral-400" data-testid="payment-timeout">Still processing. Check Plans & billing in a minute.</p>}
      <Link to="/app" className="inline-block mt-8 px-5 py-2.5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500" data-testid="payment-back-btn">Back to studying</Link>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <h1 className="font-display text-3xl font-semibold" data-testid="payment-cancel-title">Checkout canceled</h1>
      <p className="text-neutral-400 mt-2">No charge was made. You can pick a plan any time.</p>
      <div className="flex gap-3 justify-center mt-8">
        <Link to="/app/billing" className="px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5" data-testid="payment-cancel-plans-btn">See plans</Link>
        <Link to="/app" className="px-5 py-2.5 rounded-full bg-sky-400 text-white text-sm font-medium hover:bg-sky-500">Back to studying</Link>
      </div>
    </Shell>
  );
}
